import { useState } from 'react';
import { Drawer, Switch, Typography, Space, Button, Divider, Tag } from 'antd';
import { FlagOutlined } from '@ant-design/icons';
import { FEATURE_FLAG_META, type FeatureFlag } from '@/config/featureFlags';
import { resetDemoPricing } from '@/hooks/usePricing';
import { useFeatureFlags } from './FeatureFlagContext';

/**
 * Dev-only panel to toggle SenditSell feature flags (localStorage).
 * Also hosts the S1.1 "Reset demo data" control: the pricing store persists
 * demo edits across reloads (dev/mock mode), so restoring the seeds needs an
 * explicit affordance — store reset + cache clear + refetch.
 */
export function DevFlagPanel() {
  const [open, setOpen] = useState(false);
  const [resettingDemoData, setResettingDemoData] = useState(false);
  const [demoDataResetAt, setDemoDataResetAt] = useState<string | null>(null);
  const { flags, setFlag, enableDemoClosedLoop, resetToDefaults } = useFeatureFlags();

  if (!import.meta.env.DEV) return null;

  const onResetDemoData = async () => {
    setResettingDemoData(true);
    setDemoDataResetAt(null);
    try {
      await resetDemoPricing();
      setDemoDataResetAt(new Date().toLocaleTimeString());
    } finally {
      setResettingDemoData(false);
    }
  };

  const byWave = (wave: 0 | 1 | 2 | 3) =>
    (Object.keys(FEATURE_FLAG_META) as FeatureFlag[]).filter((k) => FEATURE_FLAG_META[k].wave === wave);

  return (
    <>
      <Button
        type="primary"
        shape="circle"
        icon={<FlagOutlined />}
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          right: 16,
          bottom: 16,
          zIndex: 1100,
          boxShadow: '0 4px 12px rgba(0,0,0,.2)',
        }}
        title="Feature flags"
      />
      <Drawer
        title="SenditSell feature flags"
        open={open}
        onClose={() => setOpen(false)}
        width={400}
        extra={
          <Space>
            <Button size="small" onClick={enableDemoClosedLoop}>
              Demo closed loop
            </Button>
            <Button size="small" onClick={resetToDefaults}>
              Reset
            </Button>
          </Space>
        }
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          Holistic MVP spine: masters → SO → PO (vendor) → OCC → ship/GR → finance/engines.
          Complex defaults OFF. <strong>Demo closed loop</strong> turns on Waves 1–2 only.
          Overrides persist in localStorage. Env: <code>VITE_FEATURE_FLAGS</code> or{' '}
          <code>yarn dev:mock:loop</code>.
        </Typography.Paragraph>
        {/* S1.1 — persisted demo edits (pricing dataset + audit trail) live in
            localStorage in dev/mock mode; this is the one control that drops
            them and re-hydrates the seeds. */}
        <div style={{ marginBottom: 16 }}>
          <Divider orientation="left" plain>
            Demo data
          </Divider>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Drop persisted pricing edits and restore the seeded demo dataset.
            </Typography.Text>
            <Button
              size="small"
              danger
              loading={resettingDemoData}
              onClick={() => void onResetDemoData()}
            >
              Reset demo data
            </Button>
          </div>
          {demoDataResetAt && (
            <Typography.Text type="success" style={{ fontSize: 11 }}>
              Seeds restored at {demoDataResetAt}
            </Typography.Text>
          )}
        </div>
        {([0, 1, 2, 3] as const).map((wave) => (
          <div key={wave} style={{ marginBottom: 16 }}>
            <Divider orientation="left" plain>
              Wave {wave}
            </Divider>
            {byWave(wave).map((flag) => (
              <div
                key={flag}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{FEATURE_FLAG_META[flag].label}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {flag}{' '}
                    {FEATURE_FLAG_META[flag].complex && <Tag color="orange">complex</Tag>}
                  </Typography.Text>
                </div>
                <Switch checked={flags[flag]} onChange={(v) => setFlag(flag, v)} />
              </div>
            ))}
          </div>
        ))}
      </Drawer>
    </>
  );
}
